import { Dispatch, Reducer, useCallback, useReducer } from "react";
import analytics, { trackEvent } from "src/analytics";
import { AnalyticsEvent } from "src/analytics/events";
import { dispatchify } from "src/hooks/useDispatch";
import useEffectMemo from "src/hooks/useEffectMemo";
import useMemoizer from "src/hooks/useMemoizer";
import { PageState } from "src/hooks/usePageState";

import { deepMergeWithKey, getDeep, setDeep } from "@fraction/shared";

export type PageStateActionType = "goNext" | "goBack" | "change" | "cancelEditing" | "merge" | "replace";

interface ActionMetadata<T> {
  prop?: ExtractorPath<T>;
  pagePath?: ExtractorPath<T>;
}

export interface PageStateAction<T extends object> extends ActionMetadata<T> {
  type: PageStateActionType;
  value?: any;
}

interface InnerTrackedState<T extends object> {
  __lastPageNextHit?: ExtractorPath<T>;
  __lastPageBackHit?: ExtractorPath<T>;
  __lastUpdatedKey?: ExtractorPath<T>;
  __lastAction?: PageStateActionType;
}

function reducer<T extends object>(
  state: T & InnerTrackedState<T>,
  action: PageStateAction<T>
): T & InnerTrackedState<T> {
  switch (action.type) {
    case "cancelEditing": {
      // Cancel editing
      return {
        ...state,
      };
    }
    case "change": {
      if (!action.prop) {
        throw new Error("Reducer using change without a prop name");
      }

      return {
        ...setDeep(state, action.prop, action.value),
        __lastUpdatedKey: action.prop,
        __lastAction: "change",
      };
    }
    case "goNext": {
      // for goNext, we can optionally pass a value and it's key to change the state
      const setState = action.prop !== undefined ? setDeep(state, action.prop, action.value) : state;
      return {
        ...setState,
        __lastUpdatedKey: action.prop || state.__lastUpdatedKey,
        __lastAction: "goNext",
        __lastPageNextHit: action.pagePath,
      };
    }
    case "goBack": {
      return {
        ...state,
        __lastAction: "goBack",
        __lastPageBackHit: action.pagePath,
      };
    }
    case "merge": {
      // merge with our current state overlapping the saved state
      return deepMergeWithKey(action.value, state, [
        "id",
        "crmId",
        "email.email",
        ["thoroughfare", "premise", "locality"],
      ]);
    }
    case "replace": {
      return action.value;
    }
  }
}

type NN<T> = NonNullable<T>;
type ExtractObj<S, K> = K extends keyof NN<S> ? NN<S>[K] : never;

// The goal of this is to be able to run `const foo = extract({x: {y: 2}}, ['x', y'])`
// and the type system knowing (without running the code) that foo is PageState<number>.
// Goes to a depth of 7. TODO: Figure out if there's a recursive way of doing this.
export interface Extractor<TObject> {
  <TKey1 extends keyof NN<TObject>>(path: [TKey1] | TKey1): PageState<NN<TObject>[TKey1]>;

  <TKey1 extends keyof NN<TObject>, TKey2 extends keyof NN<NN<TObject>[TKey1]>>(
    path: [TKey1, TKey2]
  ): PageState<NN<NN<TObject>[TKey1]>[TKey2]>;

  <
    TKey1 extends keyof NN<TObject>,
    TKey2 extends keyof NN<NN<TObject>[TKey1]>,
    TKey3 extends keyof NN<NN<NN<TObject>[TKey1]>[TKey2]>
  >(
    path: [TKey1, TKey2, TKey3]
  ): PageState<NN<NN<NN<TObject>[TKey1]>[TKey2]>[TKey3]>;

  <
    TKey1 extends keyof NN<TObject>,
    TKey2 extends keyof NN<NN<TObject>[TKey1]>,
    TKey3 extends keyof NN<NN<NN<TObject>[TKey1]>[TKey2]>,
    TKey4 extends keyof NN<NN<NN<NN<TObject>[TKey1]>[TKey2]>[TKey3]>
  >(
    path: [TKey1, TKey2, TKey3, TKey4]
  ): PageState<NN<NN<NN<NN<TObject>[TKey1]>[TKey2]>[TKey3]>[TKey4]>;

  <
    TKey1 extends keyof NN<TObject>,
    TKey2 extends keyof NN<NN<TObject>[TKey1]>,
    TKey3 extends keyof NN<NN<NN<TObject>[TKey1]>[TKey2]>,
    TKey4 extends keyof NN<NN<NN<NN<TObject>[TKey1]>[TKey2]>[TKey3]>,
    TKey5 extends keyof NN<NN<NN<NN<NN<TObject>[TKey1]>[TKey2]>[TKey3]>[TKey4]>
  >(
    path: [TKey1, TKey2, TKey3, TKey4, TKey5]
  ): PageState<NN<NN<NN<NN<NN<TObject>[TKey1]>[TKey2]>[TKey3]>[TKey4]>[TKey5]>;

  <
    TKey1 extends keyof NN<TObject>,
    TKey2 extends keyof NN<NN<TObject>[TKey1]>,
    TKey3 extends keyof NN<NN<NN<TObject>[TKey1]>[TKey2]>,
    TKey4 extends keyof NN<NN<NN<NN<TObject>[TKey1]>[TKey2]>[TKey3]>,
    TKey5 extends keyof NN<NN<NN<NN<NN<TObject>[TKey1]>[TKey2]>[TKey3]>[TKey4]>,
    TKey6 extends keyof NN<NN<NN<NN<NN<NN<TObject>[TKey1]>[TKey2]>[TKey3]>[TKey4]>[TKey5]>
  >(
    path: [TKey1, TKey2, TKey3, TKey4, TKey5, TKey6]
  ): PageState<NN<NN<NN<NN<NN<NN<TObject>[TKey1]>[TKey2]>[TKey3]>[TKey4]>[TKey5]>[TKey6]>;

  <
    TKey1 extends keyof NN<TObject>,
    TKey2 extends keyof NN<NN<TObject>[TKey1]>,
    TKey3 extends keyof NN<NN<NN<TObject>[TKey1]>[TKey2]>,
    TKey4 extends keyof NN<NN<NN<NN<TObject>[TKey1]>[TKey2]>[TKey3]>,
    TKey5 extends keyof NN<NN<NN<NN<NN<TObject>[TKey1]>[TKey2]>[TKey3]>[TKey4]>,
    TKey6 extends keyof NN<NN<NN<NN<NN<NN<TObject>[TKey1]>[TKey2]>[TKey3]>[TKey4]>[TKey5]>,
    TKey7 extends keyof NN<NN<NN<NN<NN<NN<NN<TObject>[TKey1]>[TKey2]>[TKey3]>[TKey4]>[TKey5]>[TKey6]>
  >(
    path: [TKey1, TKey2, TKey3, TKey4, TKey5, TKey6, TKey7]
  ): PageState<NN<NN<NN<NN<NN<NN<NN<TObject>[TKey1]>[TKey2]>[TKey3]>[TKey4]>[TKey5]>[TKey6]>[TKey7]>;

  <TKey extends ExtractorPath<TObject>>(path: TKey): PageState<ExtractObj<TObject, TKey>>;
}

export type ExtractorPath<
  TObject,
  TKey1 extends keyof TObject = keyof TObject,
  TKey2 extends keyof TObject[TKey1] = keyof TObject[TKey1],
  TKey3 extends keyof TObject[TKey1][TKey2] = keyof TObject[TKey1][TKey2],
  TKey4 extends keyof TObject[TKey1][TKey2][TKey3] = keyof TObject[TKey1][TKey2][TKey3],
  TKey5 extends keyof TObject[TKey1][TKey2][TKey3][TKey4] = keyof TObject[TKey1][TKey2][TKey3][TKey4],
  TKey6 extends keyof TObject[TKey1][TKey2][TKey3][TKey4][TKey5] = keyof TObject[TKey1][TKey2][TKey3][TKey4][TKey5],
  TKey7 extends keyof TObject[TKey1][TKey2][TKey3][TKey4][TKey5][TKey6] = keyof TObject[TKey1][TKey2][TKey3][TKey4][TKey5][TKey6]
> =
  | ([TKey1] | TKey1)
  | [TKey1, TKey2]
  | [TKey1, TKey2, TKey3]
  | [TKey1, TKey2, TKey3, TKey4]
  | [TKey1, TKey2, TKey3, TKey4, TKey5]
  | [TKey1, TKey2, TKey3, TKey4, TKey5, TKey6]
  | [TKey1, TKey2, TKey3, TKey4, TKey6, TKey6, TKey7];

export interface ReducerReturn<TObject extends object> {
  extract: Extractor<TObject>;
  state: TObject;
  dispatch: Dispatch<PageStateAction<TObject>>;
}

interface Callbacks<T extends object> {
  onNext?: (val: {
    key: string;
    value: any;
    state: T;
    dispatch: Dispatch<PageStateAction<T>>;
    prop?: ExtractorPath<T>;
  }) => void;
  onBack?: (val: { key: string; state: T; prop?: ExtractorPath<T> }) => void;
}

export default function useReducerPageState<T extends object>(
  flow: string,
  initialState: T,
  { onNext, onBack }: Callbacks<T> = {}
): ReducerReturn<T> {
  const [state, dispatch] = useReducer<Reducer<T & InnerTrackedState<T>, PageStateAction<T>>>(
    reducer,
    initialState
  );
  const memoizer = useMemoizer<(value: any) => void>();

  useEffectMemo(() => {
    const lastValue = state.__lastUpdatedKey ? getDeep(state as T, state.__lastUpdatedKey) : undefined;
    const lastKey = state.__lastUpdatedKey?.toString() || "undefined";

    switch (state.__lastAction) {
      case "goNext": {
        trackEvent(AnalyticsEvent.SUBMITTED, {
          flow,
          submittedValue: lastKey,
          pageName: state.__lastPageNextHit,
          [lastKey]: lastValue,
        });
        onNext?.({ key: lastKey, state, value: lastValue, dispatch, prop: state.__lastPageNextHit });
        break;
      }
      case "goBack": {
        analytics.track("goBack", { flow, pageName: state.__lastPageBackHit, goingBackFrom: lastKey });
        onBack?.({ key: lastKey, state, prop: state.__lastPageBackHit });
      }
    }
  }, [state.__lastAction, state.__lastPageNextHit, state.__lastPageBackHit]);

  const extract = useCallback(
    (name: ExtractorPath<T>) => {
      const value = getDeep(state, name) as ExtractObj<T, ExtractorPath<T>>;

      const dispatchified = dispatchify(value, (nextVal: ExtractObj<T, ExtractorPath<T>>) =>
        dispatch({ type: "change", prop: name, value: nextVal })
      );

      const onNextWithMeta = (...args: any) => {
        dispatch({ type: "goNext", pagePath: name, ...args });
      };

      const onBackWithMeta = (...args: any) => {
        dispatch({ type: "goBack", pagePath: name, ...args });
      };

      return {
        name: name.toString(),
        value,
        onChange: memoizer(`${name.toString()}onChange`, [dispatch], dispatchified),
        onBack: memoizer(`${name.toString()}onBack`, [dispatch], onBackWithMeta),
        onNext: memoizer(`${name.toString()}onNext`, [dispatch], onNextWithMeta),
      };
    },
    [state, memoizer]
  );

  return {
    extract: extract as Extractor<T>,
    state,
    dispatch,
  };
}
